import { UserPlus2, Link as LinkIcon } from 'lucide-react';
import { Button } from '../button/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../dialog';
import { Input } from '../input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../select';
import LoadingSpinner from '../loading-spinner';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { importAdversarialPersonas } from '../../../lib/api';
import { QuotaExceededError } from '../../../lib/custom-errors';
import { PersonaAdversarialPopupButtonProps, GeneratedPersona } from './types';
import { usePersonas } from '../../../contexts/PersonaContext';

const MAX_PERSONA_AMOUNT = 5;

const DESCRIPTION_TEXT =
  'Adversarial personas help uncover weaknesses in your Agents by simulating the kinds of users most likely to break your Evaluators. Select an Evaluator to generate personas designed to stress test that scenario.';

export const PersonaAdversarialPopupButton = ({
  workspaceId,
  evaluatorId: evaluatorIdProp,
  statement: statementProp,
  evaluators,
  tooltipContent,
  dataOnboarding,
  variant = 'icon',
}: PersonaAdversarialPopupButtonProps) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [personaAmount, setPersonaAmount] = useState('');
  const [selectedEvaluatorId, setSelectedEvaluatorId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [generatedPersonas, setGeneratedPersonas] = useState<
    GeneratedPersona[]
  >([]);

  const { fetchPersonas } = usePersonas();
  const navigate = useNavigate();

  const hasEvaluatorPicker = !!evaluators?.length;

  if (!hasEvaluatorPicker && !evaluatorIdProp) return null;

  const resolvedEvaluatorId = hasEvaluatorPicker
    ? selectedEvaluatorId
    : evaluatorIdProp;
  const resolvedStatement = hasEvaluatorPicker
    ? (evaluators?.find((e) => e.id === selectedEvaluatorId)?.statement ?? '')
    : (statementProp ?? '');

  const handleGeneratePersonas = async () => {
    setError(null);
    setSuccess(null);
    setGeneratedPersonas([]);

    if (!personaAmount) return;
    if (!resolvedEvaluatorId || !resolvedStatement) {
      setError('Please select an evaluator');
      return;
    }

    const amount = Number(personaAmount.trim());

    if (isNaN(amount) || amount < 1) {
      setError('Invalid number of personas');
      return;
    }

    if (amount > MAX_PERSONA_AMOUNT) {
      setError(`Number of personas is larger than ${MAX_PERSONA_AMOUNT}`);
      return;
    }

    setIsLoading(true);

    try {
      const result = await importAdversarialPersonas({
        workspaceId,
        key: resolvedEvaluatorId,
        statement: resolvedStatement,
        amount,
      });
      fetchPersonas();
      const personas = (result.personas ?? []) as GeneratedPersona[];
      setGeneratedPersonas(personas);
      setSuccess(
        personas.length > 0
          ? `Generated the following adversarial persona${
              personas.length === 1 ? '' : 's'
            }:`
          : `Generated adversarial personas!`,
      );
      setPersonaAmount('');
    } catch (err) {
      console.error('Error generating adversarial personas:', err);
      setError(
        err instanceof QuotaExceededError
          ? err.message
          : 'Failed to generate adversarial personas. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const canGenerate = resolvedEvaluatorId && personaAmount;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          setError(null);
          setSuccess(null);
          setGeneratedPersonas([]);
          setPersonaAmount('');
          setIsLoading(false);
        }
      }}
    >
      <span className={clsx('inline-flex', variant === 'icon' && 'rounded-md')}>
        <DialogTrigger asChild>
          {variant === 'button' ? (
            <Button
              data-onboarding={dataOnboarding}
              title={tooltipContent}
              className="hover:bg-prompt-safecomoutrageous-orange/90 bg-prompt-safecomoutrageous-orange text-white hover:text-white"
            >
              <UserPlus2 className="h-4 w-4" />
              Generate adversarial
            </Button>
          ) : (
            <Button
              data-onboarding={dataOnboarding}
              title={tooltipContent}
              aria-label="Generate adversarial personas"
              variant="ghost"
              size="sm"
              className="h-8 gap-1 px-2 text-xs text-prompt-safecomabbey hover:text-prompt-safecomoutrageous-orange"
            >
              <UserPlus2 className="h-4 w-4" />
              {/* A visible label: an icon alone was easy to miss in testing. */}
              <span className="whitespace-nowrap">Adversarial persona</span>
            </Button>
          )}
        </DialogTrigger>
      </span>

      <DialogContent className="flex flex-col gap-3">
        <DialogHeader>
          <DialogTitle>Generate adversarial personas</DialogTitle>
          <DialogDescription>{DESCRIPTION_TEXT}</DialogDescription>
        </DialogHeader>

        <div className="mt-3 flex flex-col gap-3">
          {hasEvaluatorPicker && (
            <Select
              value={selectedEvaluatorId}
              onValueChange={setSelectedEvaluatorId}
              disabled={isLoading}
            >
              <SelectTrigger className="w-full bg-prompt-safecomwhite">
                <SelectValue placeholder="Select an evaluator" />
              </SelectTrigger>
              <SelectContent>
                {evaluators.map((ev) => (
                  <SelectItem key={ev.id} value={ev.id}>
                    {ev.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Input
            disabled={isLoading}
            value={personaAmount}
            onChange={(e) => setPersonaAmount(e.target.value)}
            placeholder="Number of personas to generate"
          />

          {error && <p className="text-sm text-red-500">{error}</p>}
          {success && (
            <>
              <p className="text-sm text-green-600">{success}</p>

              {generatedPersonas.length > 0 && (
                <ul className="mt-1 flex flex-col gap-1.5">
                  {generatedPersonas.map((persona) => (
                    <li key={persona.persona_id}>
                      <Button
                        variant="ghost"
                        size="lg"
                        onClick={() => {
                          const target = `/persona-builder/${persona.persona_id}`;
                          setOpen(false);
                          setTimeout(() => navigate(target), 0);
                        }}
                        className="flex w-full items-center justify-between gap-2 rounded-md border bg-white px-2.5 py-2 text-left text-sm text-prompt-safecomabbey"
                      >
                        <span className="truncate">{persona.name}</span>
                        <LinkIcon className="h-4 w-4 shrink-0 text-prompt-safecomoutrageous-orange" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          {isLoading && (
            <div className="flex items-center gap-2 text-sm">
              <LoadingSpinner strokeWidth={7} />
              Creating adversarial personas...
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            onClick={handleGeneratePersonas}
            disabled={isLoading || !canGenerate}
            variant="outline"
            className={clsx(
              'hover:bg-prompt-safecomoutrageous-orange/90 bg-prompt-safecomoutrageous-orange text-white hover:text-white',
              isLoading && 'px-11',
            )}
          >
            {isLoading ? (
              <LoadingSpinner strokeWidth={7} />
            ) : (
              <>
                <UserPlus2 />
                Generate
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
